/**
 * Keyboard Shortcuts Context
 * Global state management for keyboard shortcuts
 */

import React, { createContext, useContext, useState, type ReactNode } from 'react';
import type { KeyboardShortcut } from '../hooks/useKeyboardShortcuts';

interface KeyboardShortcutsContextValue {
  isHelpModalOpen: boolean;
  openHelpModal: () => void;
  closeHelpModal: () => void;
  toggleHelpModal: () => void;
  registerShortcuts: (shortcuts: KeyboardShortcut[]) => void;
  allShortcuts: KeyboardShortcut[];
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | undefined>(
  undefined
);

// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its Provider (fast-refresh only)
export const useKeyboardShortcutsContext = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error(
      'useKeyboardShortcutsContext must be used within KeyboardShortcutsProvider'
    );
  }
  return context;
};

interface KeyboardShortcutsProviderProps {
  children: ReactNode;
}

export const KeyboardShortcutsProvider: React.FC<KeyboardShortcutsProviderProps> = ({
  children,
}) => {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [allShortcuts, setAllShortcuts] = useState<KeyboardShortcut[]>([]);

  const openHelpModal = () => setIsHelpModalOpen(true);
  const closeHelpModal = () => setIsHelpModalOpen(false);
  const toggleHelpModal = () => setIsHelpModalOpen((prev) => !prev);

  const registerShortcuts = (shortcuts: KeyboardShortcut[]) => {
    setAllShortcuts((prev) => {
      // Merge shortcuts, avoiding duplicates
      const existing = new Set(prev.map((s) => `${s.key}-${s.ctrl}-${s.shift}-${s.alt}`));
      const newShortcuts = shortcuts.filter(
        (s) => !existing.has(`${s.key}-${s.ctrl}-${s.shift}-${s.alt}`)
      );
      return [...prev, ...newShortcuts];
    });
  };

  const value: KeyboardShortcutsContextValue = {
    isHelpModalOpen,
    openHelpModal,
    closeHelpModal,
    toggleHelpModal,
    registerShortcuts,
    allShortcuts,
  };

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};
